import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { I18NextService } from 'angular-i18next';
import { GlobalActions, ReferencesService, RequestEventsHub, RouteActions } from 'core';
import { RoutingConfig } from 'routing.config';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

const fadeInAnimation = trigger('fadeInAnimation', [
    transition('* => *', [style({ opacity: '0' }), animate('0.4s cubic-bezier(0.4, 0.0, 0.2, 1)', style({ opacity: '1' }))])
]);
function userMustSeeNotificationFor(item: Backend.RequestListItem): boolean {
    return item.currentUserIsContactForRequest || item.requestOwnerLogin === ETR.currentUserLogin;
}

@Component({
    animations: [fadeInAnimation],
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 's7-app',
    styleUrls: ['./app.component.scss'],
    templateUrl: './app.component.html'
})
export class AppComponent implements OnInit {
    public isAuthorized: boolean = !ETR.isAnonymous;
    public isCypress: boolean = ETR.isCypress;
    public showSpinner$: Observable<boolean> = GlobalActions.SpinnerIsVisible;
    public renderDarkLayout$: Subject<boolean> = new Subject();
    public CCR01RedesignEnabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public displayHeader$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    public navigationItems$: BehaviorSubject<Backend.PortalMenuFirstLevelMenuItem[]> = new BehaviorSubject<Backend.PortalMenuFirstLevelMenuItem[]>([]);
    public CCR01RequestUrl = '/ccr01/new';
    public BSP01RequestUrl = '/bsp01/new'
    public SR01RequestUrl = '/sr01/new';
    public SR01CompleteUrl = '/sr01/complete';
    public AA01RequestUrl = '/aa01/new'

    constructor(
        private routeActs: RouteActions,
        private router: Router,
        i18Next: I18NextService,
        referencesService: ReferencesService
    ) {
        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.renderDarkLayout$.next(
                    event.urlAfterRedirects.indexOf(RoutingConfig.RequestsList.Base) !== 1 &&
                    event.urlAfterRedirects.indexOf(RoutingConfig.DepartmentTaskList.Base) !== 1
                );
            }
            const urlPath = this.router.url.split('?')[0];
            /* Если у нас анонимная страница CCR01, то добавляем класс для редизайна datepicker */
            if (!this.isAuthorized && urlPath === this.CCR01RequestUrl) {
                this.CCR01RedesignEnabled$.next(true);
                AppComponent.setCCR01RedesignClass();
            }

            const needHideHeader = !this.isAuthorized && (urlPath === this.SR01RequestUrl || urlPath === this.SR01CompleteUrl);
            this.displayHeader$.next(!needHideHeader);
        });
        if (this.isAuthorized) {
            referencesService.getPortalMenuForCurrentUser().subscribe((navigationItems: Backend.PortalMenuFirstLevelMenuItem[]) => {
                this.navigationItems$.next(navigationItems);
            });

            RequestEventsHub.requestRejectedByCarrier$.pipe(filter(userMustSeeNotificationFor)).subscribe(item => {
                GlobalActions.displayWarning(
                    i18Next.t('shared:notifications.requestRejectedByCarrier', {
                        requestLink: AppComponent.getRequestLink(item),
                        interpolation: { escapeValue: false }
                    }),
                    GlobalActions.InfiniteInterval
                );
            });
            RequestEventsHub.workflowCompleted$.pipe(filter(userMustSeeNotificationFor)).subscribe(item => {
                GlobalActions.displaySuccess(
                    i18Next.t('shared:notifications.workflowCompleted', {
                        requestLink: AppComponent.getRequestLink(item),
                        interpolation: { escapeValue: false }
                    }),
                    GlobalActions.InfiniteInterval
                );
            });
            RequestEventsHub.requestPartiallyApprovedByCarrier$.pipe(filter(userMustSeeNotificationFor)).subscribe(item => {
                GlobalActions.displaySuccess(
                    i18Next.t('shared:notifications.requestPartiallyApprovedByCarrier', {
                        requestLink: AppComponent.getRequestLink(item),
                        interpolation: { escapeValue: false }
                    }),
                    GlobalActions.InfiniteInterval
                );
            });
        }
    }

    public ngOnInit(): void {
        this.routeActs.init();
        GlobalActions.hideSpinner();
    }

    /*For animations*/
    public getState(outlet: any): any {
        return outlet.activatedRouteData.state;
    }

    private static getRequestLink(item: Backend.RequestListItem): string {
        return `<a class="link link-dotted text-nowrap" href="/${item.businessProcessType}/${item.correlationId}">${item.requestNumber}</a>`;
    }

    private static setCCR01RedesignClass(): void {
        document.getElementsByTagName('s7-app')[0].className += ' CCR01-anonymous';
    }
}
